import React, { useState, useEffect } from "react";
import { useResumeContext } from "./contexts/ResumeContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
// import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Plus, Trash } from "lucide-react";
import { useForm } from "react-hook-form";
import TextChecker from "./TextChecker";
import HoverSuggestion from "./HoverSuggestion";

const JOB_SECTORS = [
  {
    id: "tech",
    name: "Information Technology",
    keywords: ["Software", "IT", "Tech"],
  },
  {
    id: "govt",
    name: "Government/Public Sector",
    keywords: ["Government", "Public Service"],
  },
  {
    id: "finance",
    name: "Banking & Finance",
    keywords: ["Banking", "Finance", "Investment"],
  },
  {
    id: "healthcare",
    name: "Healthcare",
    keywords: ["Medical", "Healthcare", "Hospital"],
  },
  {
    id: "education",
    name: "Education",
    keywords: ["Teaching", "Education", "Academic"],
  },
  {
    id: "manufacturing",
    name: "Manufacturing",
    keywords: ["Production", "Manufacturing", "Industrial"],
  },
  { id: "retail", name: "Retail", keywords: ["Sales", "Retail", "Commerce"] },
  {
    id: "consulting",
    name: "Consulting",
    keywords: ["Consulting", "Advisory"],
  },
  {
    id: "media",
    name: "Media & Entertainment",
    keywords: ["Media", "Entertainment", "Digital"],
  },
  {
    id: "nonprofit",
    name: "Non-Profit",
    keywords: ["NGO", "Non-Profit", "Social Service"],
  },
];

const RESUME_KEYWORDS = {
  skills: [
    // Technical Skills
    "Python",
    "Java",
    "JavaScript",
    "React",
    "Node.js",
    "Angular",
    "Vue.js",
    "PHP",
    "Ruby",
    "C++",
    "AWS",
    "Azure",
    "GCP",
    "Docker",
    "Kubernetes",
    "DevOps",
    "CI/CD",
    "Git",
    "REST API",
    "GraphQL",
    "SQL",
    "NoSQL",
    "MongoDB",
    "PostgreSQL",
    "MySQL",
    "Redis",
    "Data Analysis",
    "Machine Learning",
    "Artificial Intelligence",
    "Deep Learning",
    "Natural Language Processing",
    "Computer Vision",
    "Blockchain",
    "Cybersecurity",
    "Network Security",
    "Cloud Computing",
    "Microservices",
    "System Design",
    "Software Architecture",
    "Full Stack Development",
    "Mobile Development",
    "iOS",
    "Android",
    "React Native",
    "Flutter",
    "UI/UX Design",
    "Responsive Design",

    // Business & Management
    "Project Management",
    "Agile",
    "Scrum",
    "Kanban",
    "JIRA",
    "Confluence",
    "Team Leadership",
    "Strategic Planning",
    "Business Analysis",
    "Requirements Gathering",
    "Risk Management",
    "Stakeholder Management",
    "Budget Management",
    "Resource Planning",
    "Change Management",
    "Process Improvement",
    "Quality Assurance",
    "Six Sigma",
    "Lean Management",

    // Soft Skills
    "Communication",
    "Leadership",
    "Problem Solving",
    "Critical Thinking",
    "Teamwork",
    "Time Management",
    "Adaptability",
    "Creativity",
    "Innovation",
    "Decision Making",
    "Conflict Resolution",
    "Negotiation",
    "Presentation Skills",
    "Public Speaking",

    // Government & Public Sector
    "Public Policy",
    "Public Administration",
    "Government Relations",
    "Policy Analysis",
    "Regulatory Compliance",
    "Legislative Affairs",
    "Grant Writing",
    "Program Management",
    "Emergency Management",
    "Public Safety",
    "Civil Service",
    "Municipal Administration",

    // Finance & Banking
    "Financial Analysis",
    "Investment Banking",
    "Risk Assessment",
    "Portfolio Management",
    "Financial Planning",
    "Wealth Management",
    "Trading",
    "Securities",
    "Derivatives",
    "Corporate Finance",
    "Mergers & Acquisitions",
    "Private Equity",
    "Venture Capital",

    // Healthcare
    "Healthcare Management",
    "Clinical Research",
    "Medical Records",
    "Patient Care",
    "Healthcare IT",
    "HIPAA Compliance",
    "Electronic Health Records",
    "Telemedicine",

    // Education
    "Teaching",
    "Curriculum Development",
    "Educational Technology",
    "E-Learning",
    "Instructional Design",
    "Student Assessment",
    "Academic Advising",
    "Research",

    // Certifications
    "PMP",
    "CISSP",
    "AWS Certified",
    "Azure Certified",
    "Google Certified",
    "Scrum Master",
    "Six Sigma",
    "ITIL",
    "CompTIA",
    "CISA",
    "CFA",
    "CPA",
    "PHR",
    "SHRM",
  ],

  jobTitles: [
    // Technical Roles
    "Software Engineer",
    "Full Stack Developer",
    "Data Scientist",
    "DevOps Engineer",
    "Cloud Architect",
    "Security Engineer",
    "Machine Learning Engineer",
    "AI Specialist",
    "Database Administrator",
    "Systems Administrator",
    "Network Engineer",
    "UI/UX Designer",

    // Management Roles
    "Project Manager",
    "Product Manager",
    "Program Manager",
    "Engineering Manager",
    "Technical Lead",
    "CTO",
    "CIO",
    "IT Director",
    "VP of Engineering",
    "Scrum Master",

    // Government Roles
    "Policy Analyst",
    "Program Coordinator",
    "Public Administrator",
    "Government Affairs",
    "Civil Service Officer",
    "Municipal Manager",
    "Public Safety Director",

    // Finance Roles
    "Financial Analyst",
    "Investment Banker",
    "Risk Manager",
    "Portfolio Manager",
    "Financial Advisor",
    "Wealth Manager",
    "Trading Analyst",
    "Investment Associate",

    // Healthcare Roles
    "Healthcare Administrator",
    "Clinical Director",
    "Medical Manager",
    "Healthcare IT Specialist",
    "Clinical Systems Analyst",

    // Education Roles
    "Professor",
    "Instructor",
    "Educational Technologist",
    "Academic Director",
    "Research Scientist",
    "Department Head",
    "Dean",
  ],
};

const SkillsSection = ({ skills, updateSkills }) => {
  const [sections, setSections] = useState(skills?.sections || {});
  const [newSectionTitle, setNewSectionTitle] = useState("");
  const [newSkill, setNewSkill] = useState("");
  const [currentSection, setCurrentSection] = useState("");
  const [tempSkills, setTempSkills] = useState({});

  // Initialize tempSkills with existing sections
  useEffect(() => {
    const initialTempSkills = {};
    Object.keys(sections).forEach((section) => {
      initialTempSkills[section] = [...sections[section]];
    });
    setTempSkills(initialTempSkills);
  }, []);

  const handleAddSection = (e) => {
    e.preventDefault();
    if (!newSectionTitle.trim()) return;

    if (!sections[newSectionTitle]) {
      const updatedSections = {
        ...sections,
        [newSectionTitle]: [],
      };
      setSections(updatedSections);
      setTempSkills((prev) => ({
        ...prev,
        [newSectionTitle]: [],
      }));
      updateSkills({ sections: updatedSections });
      setCurrentSection(newSectionTitle);
    }
    setNewSectionTitle("");
  };

  const handleSkillInput = (e, section) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addSkillToSection(section);
    }
  };

  const addSkillToSection = (section) => {
    const skillToAdd = newSkill.trim().replace(/,/g, "");
    if (!skillToAdd) return;

    if (sections[section] && !sections[section].includes(skillToAdd)) {
      const updatedSections = {
        ...sections,
        [section]: [...sections[section], skillToAdd],
      };
      setSections(updatedSections);
      updateSkills({ sections: updatedSections });
    }
    setNewSkill("");
  };

  const removeSkill = (section, skillToRemove) => {
    const updatedSections = {
      ...sections,
      [section]: sections[section].filter((skill) => skill !== skillToRemove),
    };
    setSections(updatedSections);
    updateSkills({ sections: updatedSections });
  };

  const removeSection = (sectionTitle) => {
    const updatedSections = { ...sections };
    delete updatedSections[sectionTitle];
    setSections(updatedSections);
    updateSkills({ sections: updatedSections });
    if (currentSection === sectionTitle) {
      setCurrentSection("");
    }
  };

  return (
    <div className="space-y-6">
      {/* Add New Section Form */}
      <Card className="p-4">
        <form onSubmit={handleAddSection} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sectionTitle">Add New Skills Section</Label>
            <div className="flex gap-2">
              <Input
                id="sectionTitle"
                value={newSectionTitle}
                onChange={(e) => setNewSectionTitle(e.target.value)}
                placeholder="e.g., Programming Languages"
                className="flex-1"
              />
              <Button type="submit">Add Section</Button>
            </div>
          </div>
        </form>
      </Card>

      {/* Skills Sections */}
      <div className="space-y-4">
        {Object.entries(sections).map(([title, skillsList]) => (
          <Card key={title} className="p-4">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-bold text-base">{title}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeSection(title)}
                  className="text-red-500 hover:text-red-700"
                >
                  Remove Section
                </Button>
              </div>

              {/* Skills Input */}
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    placeholder="Type a skill and press Enter or comma to add"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyDown={(e) => handleSkillInput(e, title)}
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Press Enter or use comma (,) to add skills
                </p>
              </div>

              {/* Skills Display */}
              <div className="flex flex-wrap gap-2">
                {skillsList.map((skill, index) => (
                  <div
                    key={index}
                    className="group flex items-center bg-primary/10 hover:bg-primary/20 text-primary rounded-full pl-3 pr-2 py-1 text-sm transition-colors"
                  >
                    <span>{skill}</span>
                    <button
                      onClick={() => removeSkill(title, skill)}
                      className="ml-2 w-4 h-4 flex items-center justify-center rounded-full hover:bg-red-100 hover:text-red-600 transition-colors"
                      title="Remove skill"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {Object.keys(sections).length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No skills sections added. Add your first skills section above.
        </div>
      )}
    </div>
  );
};

const ResumeForm = () => {
  const {
    resumeData,
    updatePersonalInfo,
    updateSummary,
    updateSkills,
    addExperience,
    updateExperience,
    removeExperience,
    addProject,
    updateProject,
    removeProject,
    addEducation,
    updateEducation,
    removeEducation,
    addCertification,
    updateCertification,
    removeCertification,
    resetToTemplate,
  } = useResumeContext();

  // Create a form instance for react-hook-form
  const form = useForm();

  const [newSkill, setNewSkill] = useState("");
  const [skillError, setSkillError] = useState("");
  const [selectedSector, setSelectedSector] = useState("");
  const [suggestedKeywords, setSuggestedKeywords] = useState([]);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 50; year--) {
      years.push(year);
    }
    return years;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return { month: "", year: "" };
    try {
      const [year, month] = dateString.split("-");
      const monthIndex = parseInt(month) - 1;
      if (isNaN(monthIndex) || monthIndex < 0 || monthIndex >= 12)
        return { month: "", year: "" };
      return {
        month: months[monthIndex],
        year: year,
      };
    } catch (error) {
      return { month: "", year: "" };
    }
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";
    const { month, year } = formatDateForInput(dateString);
    if (month && year) {
      return `${month} ${year}`;
    }
    return "";
  };

  const handleDateChange = (id, type, value, dateType) => {
    const isExperience = resumeData.experience.some((exp) => exp.id === id);
    const item = isExperience
      ? resumeData.experience.find((exp) => exp.id === id)
      : resumeData.education.find((edu) => edu.id === id);

    if (!item) return;

    // Get current date values
    const currentDate = formatDateForInput(item[dateType]);
    let month = type === "month" ? value : currentDate.month;
    let year = type === "year" ? value : currentDate.year;

    // Create the new date string
    let dateValue = "";
    if (month && year) {
      const monthIndex = months.indexOf(month) + 1;
      if (monthIndex > 0) {
        dateValue = `${year}-${monthIndex.toString().padStart(2, "0")}`;
      }
    }

    // Update the state
    if (isExperience) {
      const updatedExp = {
        ...item,
        [dateType]: dateValue,
      };
      updateExperience(id, updatedExp);
    } else {
      const updatedEdu = {
        ...item,
        [dateType]: dateValue,
      };
      updateEducation(id, updatedEdu);
    }
  };

  const handleAddSkill = (e) => {
    if (e.key === "Enter" && newSkill.trim()) {
      e.preventDefault();
      const skillToAdd = newSkill.trim();

      // Check for duplicates (case-insensitive)
      if (
        resumeData.skills.some(
          (skill) => skill.toLowerCase() === skillToAdd.toLowerCase()
        )
      ) {
        setSkillError("This skill has already been added");
        setTimeout(() => setSkillError(""), 2000); // Clear error after 2 seconds
        return;
      }

      const updatedSkills = [...resumeData.skills, skillToAdd];
      updateSkills(updatedSkills);
      setNewSkill("");
      setSkillError("");
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    const updatedSkills = resumeData.skills.filter(
      (skill) => skill !== skillToRemove
    );
    updateSkills(updatedSkills);
  };

  const handleAchievementsChange = (id, value) => {
    const experience = resumeData.experience.find((exp) => exp.id === id);
    if (experience) {
      // Do NOT trim or filter lines, just split by \n
      const achievements = value.split("\n");
      updateExperience(id, { achievements });
    }
  };

  const handleTechnologiesChange = (id, value) => {
    const project = resumeData.projects.find((proj) => proj.id === id);
    if (project) {
      updateProject(id, { ...project, currentTechInput: value });
    }
  };

  const handleTechnologyKeyDown = (id, e) => {
    const project = resumeData.projects.find((proj) => proj.id === id);
    if (!project) return;

    // Handle Enter key or comma key
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const value = (project.currentTechInput || "").trim();
      if (value) {
        const technologies = [...(project.technologies || [])];
        if (!technologies.includes(value)) {
          technologies.push(value);
          updateProject(id, {
            ...project,
            technologies,
            currentTechInput: "",
          });
        }
      }
    }
  };

  const removeTechnology = (projectId, techToRemove) => {
    const project = resumeData.projects.find((proj) => proj.id === projectId);
    if (project) {
      const technologies = project.technologies.filter(
        (tech) => tech !== techToRemove
      );
      updateProject(projectId, { ...project, technologies });
    }
  };

  const handleSectorChange = (sectorId) => {
    setSelectedSector(sectorId);
    const sector = JOB_SECTORS.find((s) => s.id === sectorId);
    if (sector) {
      // Filter relevant keywords based on sector
      const relevantKeywords = RESUME_KEYWORDS.skills.filter((keyword) =>
        sector.keywords.some((key) =>
          keyword.toLowerCase().includes(key.toLowerCase())
        )
      );
      setSuggestedKeywords(relevantKeywords);
    }
  };

  const renderSectorAndKeywords = () => (
    <div className="space-y-4 mt-6">
      <div className="space-y-2">
        <Label>Job Sector</Label>
        <select
          value={selectedSector}
          onChange={(e) => handleSectorChange(e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors text-black"
        >
          <option value="" style={{ color: "#6B7280" }}>
            Select a sector
          </option>
          {JOB_SECTORS.map((sector) => (
            <option key={sector.id} value={sector.id}>
              {sector.name}
            </option>
          ))}
        </select>
      </div>

      {suggestedKeywords.length > 0 && (
        <div className="space-y-2">
          <Label>Suggested Keywords</Label>
          <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-gray-50">
            {suggestedKeywords.map((keyword, index) => (
              <button
                key={index}
                onClick={() => {
                  if (!resumeData.skills.includes(keyword)) {
                    updateSkills([...resumeData.skills, keyword]);
                  }
                }}
                className="px-2 py-1 text-sm bg-white border rounded-md hover:bg-primary/10 hover:text-primary transition-colors"
                title="Click to add to skills"
              >
                {keyword}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-0 bg-background">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Resume Editor</h2>
        <Button variant="outline" onClick={resetToTemplate}>
          Reset to Template
        </Button>
      </div>

      <Tabs defaultValue="personal" className="w-full p-0">
        <TabsList className="flex flex-wrap justify-start gap-2 mb-4 p-0">
          <TabsTrigger
            value="personal"
            className="rounded-lg bg-muted hover:bg-muted/70 text-sm font-medium py-2 px-4 transition-all duration-200 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-md"
          >
            Personal
          </TabsTrigger>
          <TabsTrigger
            value="summary"
            className="rounded-lg bg-muted hover:bg-muted/70 text-sm font-medium py-2 px-4 transition-all duration-200 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-md"
          >
            Summary
          </TabsTrigger>
          <TabsTrigger
            value="skills"
            className="rounded-lg bg-muted hover:bg-muted/70 text-sm font-medium py-2 px-4 transition-all duration-200 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-md"
          >
            Skills
          </TabsTrigger>
          <TabsTrigger
            value="experience"
            className="rounded-lg bg-muted hover:bg-muted/70 text-sm font-medium py-2 px-4 transition-all duration-200 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-md"
          >
            Experience
          </TabsTrigger>
          <TabsTrigger
            value="projects"
            className="rounded-lg bg-muted hover:bg-muted/70 text-sm font-medium py-2 px-4 transition-all duration-200 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-md"
          >
            Projects
          </TabsTrigger>
          <TabsTrigger
            value="education"
            className="rounded-lg bg-muted hover:bg-muted/70 text-sm font-medium py-2 px-4 transition-all duration-200 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-md"
          >
            Education
          </TabsTrigger>
          <TabsTrigger
            value="certifications"
            className="rounded-lg bg-muted hover:bg-muted/70 text-sm font-medium py-2 px-4 transition-all duration-200 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-md"
          >
            Certifications
          </TabsTrigger>
        </TabsList>

        {/* Personal Info */}
        <TabsContent value="personal" className="p-0">
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    value={resumeData.personalInfo.fullName}
                    onChange={(e) =>
                      updatePersonalInfo({ fullName: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="title">Professional Title</Label>
                  <Input
                    id="title"
                    value={resumeData.personalInfo.title}
                    onChange={(e) =>
                      updatePersonalInfo({ title: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={resumeData.personalInfo.email}
                    onChange={(e) =>
                      updatePersonalInfo({ email: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={resumeData.personalInfo.phone}
                    onChange={(e) =>
                      updatePersonalInfo({ phone: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={resumeData.personalInfo.location}
                    onChange={(e) =>
                      updatePersonalInfo({ location: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Website (optional)</Label>
                  <Input
                    id="website"
                    value={resumeData.personalInfo.website || ""}
                    onChange={(e) =>
                      updatePersonalInfo({ website: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="linkedin">LinkedIn (optional)</Label>
                  <Input
                    id="linkedin"
                    value={resumeData.personalInfo.linkedin || ""}
                    onChange={(e) =>
                      updatePersonalInfo({ linkedin: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="github">GitHub (optional)</Label>
                  <Input
                    id="github"
                    value={resumeData.personalInfo.github || ""}
                    onChange={(e) =>
                      updatePersonalInfo({ github: e.target.value })
                    }
                  />
                </div>
                <div className="flex flex-col space-y-2">
                  <label
                    htmlFor="profileImage"
                    className="cursor-pointer inline-block px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow hover:bg-blue-700 transition duration-200 text-center"
                  >
                    Upload Profile Image (optional)
                  </label>
                  <input
                    id="profileImage"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        updatePersonalInfo({ profileImage: file });
                      }
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Summary */}
        <TabsContent value="summary">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Professional Summary</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="summary">Career Summary</Label>
                <div className="relative">
                  <Textarea
                    id="summary"
                    rows={5}
                    value={resumeData.summary}
                    onChange={(e) => updateSummary(e.target.value)}
                    placeholder="Write a compelling summary of your professional background and goals..."
                  />
                  <TextChecker text={resumeData.summary} maxLength={2000} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Skills */}
        <TabsContent value="skills">
          <Card>
            <CardHeader>
              <CardTitle>Skills</CardTitle>
            </CardHeader>
            <CardContent>
              <SkillsSection
                skills={resumeData.skills}
                updateSkills={updateSkills}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Experience */}
        <TabsContent value="experience">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Work Experience</h3>
            <Button onClick={addExperience} size="sm">
              <Plus className="h-4 w-4 mr-2" /> Add Experience
            </Button>
          </div>

          {resumeData.experience.map((exp) => (
            <Card key={exp.id} className="mb-4">
              <CardHeader className="pb-2">
                <div className="flex justify-between">
                  <CardTitle className="text-base">
                    Experience Details
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeExperience(exp.id)}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`company-${exp.id}`}>Company</Label>
                    <Input
                      id={`company-${exp.id}`}
                      value={exp.company}
                      onChange={(e) =>
                        updateExperience(exp.id, { company: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`position-${exp.id}`}>Position</Label>
                    <Input
                      id={`position-${exp.id}`}
                      value={exp.position}
                      onChange={(e) =>
                        updateExperience(exp.id, { position: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`location-${exp.id}`}>Location</Label>
                    <Input
                      id={`location-${exp.id}`}
                      value={exp.location}
                      onChange={(e) =>
                        updateExperience(exp.id, { location: e.target.value })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-1">
                      <Label
                        htmlFor={`startDate-${exp.id}`}
                        className="flex justify-between text-xs"
                      >
                        <span>Start Date</span>
                        {formatDisplayDate(exp.startDate) && (
                          <span className="text-xs text-primary">
                            {formatDisplayDate(exp.startDate)}
                          </span>
                        )}
                      </Label>
                      <div className="grid grid-cols-2 gap-1">
                        <select
                          id={`startMonth-${exp.id}`}
                          value={formatDateForInput(exp.startDate).month || ""}
                          onChange={(e) =>
                            handleDateChange(
                              exp.id,
                              "month",
                              e.target.value,
                              "startDate"
                            )
                          }
                          className="w-full px-2 py-1 text-xs rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent bg-white text-gray-900 appearance-none cursor-pointer hover:border-primary transition-colors"
                        >
                          <option value="">Month</option>
                          {months.map((month) => (
                            <option
                              key={month}
                              value={month}
                              className="text-gray-900"
                            >
                              {month}
                            </option>
                          ))}
                        </select>
                        <select
                          id={`startYear-${exp.id}`}
                          value={formatDateForInput(exp.startDate).year || ""}
                          onChange={(e) =>
                            handleDateChange(
                              exp.id,
                              "year",
                              e.target.value,
                              "startDate"
                            )
                          }
                          className="w-full px-2 py-1 text-xs rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent bg-white text-gray-900 appearance-none cursor-pointer hover:border-primary transition-colors"
                        >
                          <option value="">Year</option>
                          {generateYearOptions().map((year) => (
                            <option
                              key={year}
                              value={year}
                              className="text-gray-900"
                            >
                              {year}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label
                        htmlFor={`endDate-${exp.id}`}
                        className="flex justify-between text-xs"
                      >
                        <span>End Date</span>
                        {exp.currentlyWorking ? (
                          <span className="text-xs text-primary">Present</span>
                        ) : (
                          formatDisplayDate(exp.endDate) && (
                            <span className="text-xs text-primary">
                              {formatDisplayDate(exp.endDate)}
                            </span>
                          )
                        )}
                      </Label>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 mb-1">
                          <input
                            type="checkbox"
                            id={`currentlyWorking-${exp.id}`}
                            checked={exp.currentlyWorking || false}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              updateExperience(exp.id, {
                                currentlyWorking: isChecked,
                                endDate: isChecked ? "" : exp.endDate,
                              });
                            }}
                            className="h-3 w-3 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                          />
                          <Label
                            htmlFor={`currentlyWorking-${exp.id}`}
                            className="text-xs cursor-pointer"
                          >
                            Currently Working
                          </Label>
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                          <select
                            id={`endMonth-${exp.id}`}
                            value={
                              exp.currentlyWorking
                                ? ""
                                : formatDateForInput(exp.endDate).month || ""
                            }
                            onChange={(e) =>
                              handleDateChange(
                                exp.id,
                                "month",
                                e.target.value,
                                "endDate"
                              )
                            }
                            disabled={exp.currentlyWorking}
                            className="w-full px-2 py-1 text-xs rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent bg-white text-gray-900 appearance-none cursor-pointer hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <option value="">Month</option>
                            {months.map((month) => (
                              <option
                                key={month}
                                value={month}
                                className="text-gray-900"
                              >
                                {month}
                              </option>
                            ))}
                          </select>
                          <select
                            id={`endYear-${exp.id}`}
                            value={
                              exp.currentlyWorking
                                ? ""
                                : formatDateForInput(exp.endDate).year || ""
                            }
                            onChange={(e) =>
                              handleDateChange(
                                exp.id,
                                "year",
                                e.target.value,
                                "endDate"
                              )
                            }
                            disabled={exp.currentlyWorking}
                            className="w-full px-2 py-1 text-xs rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent bg-white text-gray-900 appearance-none cursor-pointer hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <option value="">Year</option>
                            {generateYearOptions().map((year) => (
                              <option
                                key={year}
                                value={year}
                                className="text-gray-900"
                              >
                                {year}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-full mt-1">
                    {(exp.startDate || exp.endDate) && (
                      <p className="text-xs text-gray-600">
                        Selected Period:{" "}
                        {formatDisplayDate(exp.startDate) || "Not set"} -{" "}
                        {exp.currentlyWorking
                          ? "Present"
                          : formatDisplayDate(exp.endDate) || "Not set"}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2 mt-4">
                  <Label htmlFor={`achievements-${exp.id}`}>Achievements</Label>
                  <div className="relative">
                    <Textarea
                      id={`achievements-${exp.id}`}
                      rows={4}
                      value={exp.achievements.join("\n")}
                      onChange={(e) =>
                        handleAchievementsChange(exp.id, e.target.value)
                      }
                      placeholder="• Increased sales by 20%&#10;• Led team of 5 developers"
                    />
                    <TextChecker
                      text={exp.achievements.join("\n")}
                      maxLength={1000}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {resumeData.experience.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No experience added. Click "Add Experience" to add your work
              history.
            </div>
          )}
        </TabsContent>

        {/* Projects */}
        <TabsContent value="projects">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Projects</h3>
            <Button onClick={addProject} size="sm">
              <Plus className="h-4 w-4 mr-2" /> Add Project
            </Button>
          </div>

          {resumeData.projects.map((project) => (
            <Card key={project.id} className="mb-4">
              <CardHeader className="pb-2">
                <div className="flex justify-between">
                  <CardTitle className="text-base">Project Details</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeProject(project.id)}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center mb-2">
                      <Label htmlFor={`name-${project.id}`}>Project Name</Label>
                    </div>
                    <Input
                      id={`name-${project.id}`}
                      value={project.name}
                      onChange={(e) =>
                        updateProject(project.id, { name: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center mb-2">
                      <Label htmlFor={`description-${project.id}`}>
                        Project Description (Max 1000 characters)
                      </Label>
                    </div>
                    <div className="relative">
                      <Textarea
                        id={`description-${project.id}`}
                        value={project.description || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= 1000) {
                            updateProject(project.id, { description: value });
                          }
                        }}
                        placeholder={`• Built a full-stack web app\n• Integrated payment gateway`}
                        className="min-h-[100px]"
                      />
                      <TextChecker
                        text={project.description || ""}
                        maxLength={1000}
                      />
                    </div>
                    <div className="text-sm text-gray-500 flex justify-end">
                      {(project.description || "").length}/1000
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`technologies-${project.id}`}>
                      Technologies
                    </Label>
                    <Input
                      id={`technologies-${project.id}`}
                      value={project.currentTechInput || ""}
                      onChange={(e) =>
                        handleTechnologiesChange(project.id, e.target.value)
                      }
                      onKeyDown={(e) => handleTechnologyKeyDown(project.id, e)}
                      placeholder="Type a technology and press Enter or comma to add"
                      className="w-full px-3 py-2"
                    />
                    <p className="text-xs text-gray-500">
                      Press Enter or comma (,) to add a technology
                    </p>

                    <div className="flex flex-wrap gap-2 mt-2">
                      {(project.technologies || []).map((tech, index) => (
                        <div
                          key={index}
                          className="group flex items-center bg-blue-50 text-blue-700 rounded-full pl-3 pr-2 py-1 text-sm"
                        >
                          <span>{tech}</span>
                          <button
                            onClick={() => removeTechnology(project.id, tech)}
                            className="ml-2 w-4 h-4 flex items-center justify-center rounded-full hover:bg-blue-200 transition-colors"
                            title="Remove technology"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`link-${project.id}`}>
                      Project Link (optional)
                    </Label>
                    <Input
                      id={`link-${project.id}`}
                      value={project.link || ""}
                      onChange={(e) =>
                        updateProject(project.id, { link: e.target.value })
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {resumeData.projects.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No projects added. Click "Add Project" to showcase your work.
            </div>
          )}
        </TabsContent>

        {/* Education */}
        <TabsContent value="education">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Education</h3>
            <Button onClick={addEducation} size="sm">
              <Plus className="h-4 w-4 mr-2" /> Add Education
            </Button>
          </div>

          {resumeData.education.map((edu) => (
            <Card key={edu.id} className="mb-4">
              <CardHeader className="pb-2">
                <div className="flex justify-between">
                  <CardTitle className="text-base">Education Details</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeEducation(edu.id)}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center mb-2">
                      <Label htmlFor={`school-${edu.id}`}>
                        School/University
                      </Label>
                    </div>
                    <Input
                      id={`school-${edu.id}`}
                      value={edu.school}
                      onChange={(e) =>
                        updateEducation(edu.id, { school: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center mb-2">
                      <Label htmlFor={`degree-${edu.id}`}>Degree</Label>
                    </div>
                    <Input
                      id={`degree-${edu.id}`}
                      value={edu.degree}
                      onChange={(e) =>
                        updateEducation(edu.id, { degree: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`fieldOfStudy-${edu.id}`}>
                      Field of Study
                    </Label>
                    <Input
                      id={`fieldOfStudy-${edu.id}`}
                      value={edu.fieldOfStudy}
                      onChange={(e) =>
                        updateEducation(edu.id, {
                          fieldOfStudy: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label
                        htmlFor={`startDate-${edu.id}`}
                        className="flex justify-between text-xs"
                      >
                        <span>Start Date</span>
                        {formatDisplayDate(edu.startDate) && (
                          <span className="text-xs text-primary">
                            {formatDisplayDate(edu.startDate)}
                          </span>
                        )}
                      </Label>
                      <div className="grid grid-cols-2 gap-1">
                        <select
                          value={formatDateForInput(edu.startDate).month}
                          onChange={(e) =>
                            handleDateChange(
                              edu.id,
                              "month",
                              e.target.value,
                              "startDate"
                            )
                          }
                          className="w-full px-2 py-1 text-xs rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent bg-white text-gray-900 appearance-none cursor-pointer hover:border-primary transition-colors"
                        >
                          <option value="">Month</option>
                          {months.map((month) => (
                            <option
                              key={month}
                              value={month}
                              className="text-gray-900"
                            >
                              {month}
                            </option>
                          ))}
                        </select>
                        <select
                          value={formatDateForInput(edu.startDate).year}
                          onChange={(e) =>
                            handleDateChange(
                              edu.id,
                              "year",
                              e.target.value,
                              "startDate"
                            )
                          }
                          className="w-full px-2 py-1 text-xs rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent bg-white text-gray-900 appearance-none cursor-pointer hover:border-primary transition-colors"
                        >
                          <option value="">Year</option>
                          {generateYearOptions().map((year) => (
                            <option
                              key={year}
                              value={year}
                              className="text-gray-900"
                            >
                              {year}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label
                        htmlFor={`endDate-${edu.id}`}
                        className="flex justify-between text-xs"
                      >
                        <span>End Date</span>
                        {formatDisplayDate(edu.endDate) && (
                          <span className="text-xs text-primary">
                            {formatDisplayDate(edu.endDate)}
                          </span>
                        )}
                      </Label>
                      <div className="grid grid-cols-2 gap-1">
                        <select
                          value={formatDateForInput(edu.endDate).month}
                          onChange={(e) =>
                            handleDateChange(
                              edu.id,
                              "month",
                              e.target.value,
                              "endDate"
                            )
                          }
                          className="w-full px-2 py-1 text-xs rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent bg-white text-gray-900 appearance-none cursor-pointer hover:border-primary transition-colors"
                        >
                          <option value="">Month</option>
                          {months.map((month) => (
                            <option
                              key={month}
                              value={month}
                              className="text-gray-900"
                            >
                              {month}
                            </option>
                          ))}
                        </select>
                        <select
                          value={formatDateForInput(edu.endDate).year}
                          onChange={(e) =>
                            handleDateChange(
                              edu.id,
                              "year",
                              e.target.value,
                              "endDate"
                            )
                          }
                          className="w-full px-2 py-1 text-xs rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent bg-white text-gray-900 appearance-none cursor-pointer hover:border-primary transition-colors"
                        >
                          <option value="">Year</option>
                          {generateYearOptions().map((year) => (
                            <option
                              key={year}
                              value={year}
                              className="text-gray-900"
                            >
                              {year}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-full mt-1">
                    {(edu.startDate || edu.endDate) && (
                      <p className="text-xs text-gray-600">
                        Selected Period:{" "}
                        {formatDisplayDate(edu.startDate) || "Not set"} -{" "}
                        {formatDisplayDate(edu.endDate) || "Not set"}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {resumeData.education.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No education added. Click "Add Education" to add your educational
              background.
            </div>
          )}
        </TabsContent>

        {/* Certifications */}
        <TabsContent value="certifications">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Certifications</h3>
            <Button onClick={addCertification} size="sm">
              <Plus className="h-4 w-4 mr-2" /> Add Certification
            </Button>
          </div>

          {resumeData.certifications.map((cert) => (
            <Card key={cert.id} className="mb-4">
              <CardHeader className="pb-2">
                <div className="flex justify-between">
                  <CardTitle className="text-base">
                    Certification Details
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCertification(cert.id)}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`name-${cert.id}`}>
                      Certification Name
                    </Label>
                    <Input
                      id={`name-${cert.id}`}
                      value={cert.name}
                      onChange={(e) =>
                        updateCertification(cert.id, { name: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`issuer-${cert.id}`}>
                      Issuing Organization
                    </Label>
                    <Input
                      id={`issuer-${cert.id}`}
                      value={cert.issuer}
                      onChange={(e) =>
                        updateCertification(cert.id, { issuer: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`date-${cert.id}`}>Date Earned</Label>
                    <Input
                      id={`date-${cert.id}`}
                      type="month"
                      value={cert.date}
                      onChange={(e) =>
                        updateCertification(cert.id, { date: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`certLink-${cert.id}`}>
                      Certificate Link
                    </Label>
                    <Input
                      id={`certLink-${cert.id}`}
                      type="url"
                      placeholder="https://drive.google.com/..."
                      value={cert.certLink || ""}
                      onChange={(e) =>
                        updateCertification(cert.id, {
                          certLink: e.target.value,
                        })
                      }
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500">
                      Add your certificate link (Google Drive, Dropbox, etc.)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {resumeData.certifications.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No certifications added. Click "Add Certification" to showcase
              your credentials.
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ResumeForm;
